import {
  Component,
  Input,
  EventEmitter,
  Output,
  OnInit,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  trigger,
  keyframes,
  style,
  animate,
  transition
} from '@angular/animations';
import { Raffle, RevealRaffle } from '@core/models/raffle.model';
import { ItemCardComponent } from './components/item-card/item-card.component';
import { PromoCodeComponent } from './components/promo-code/promo-code.component';
import { HeadsUpComponent } from './components/heads-up/heads-up.component';
import { User } from '@core/models/user.model';
import { forkJoin, Observable, of, Subject, from } from 'rxjs';
import { Select } from '@ngxs/store';
import { AuthState } from '@store/auth/auth.state';
import { Cart, CartItem } from '@core/models/cart.model';
import { SvgIconComponent } from '@components/svg-icon/svg-icon.component';
import {
  PaymentGatewayName,
  PaymentSubmission
} from '@core/models/payment-submission.model';
import { GonativeService } from '@core/services/gonative/gonative.service';
import { RaffleService } from '@core/services/raffle/raffle.service';
import { PaymentCard } from '@core/models/payment-card.model';
import { TokenCardEditComponent } from './components/token-card-edit/token-card-edit.component';
import { UserService } from '@core/services/user/user.service';
import { EmptyCartService } from '@core/services/cart/empty-cart.service';
import { catchError, switchMap, takeUntil, concatMap } from 'rxjs/operators';
import { ProjectCode } from '@core/models';

@Component({
  standalone: true,
  selector: 'app-modal-repeat-purchase',
  templateUrl: './modal-repeat-purchase.component.html',
  styleUrls: ['./modal-repeat-purchase.component.scss'],
  imports: [
    CommonModule,
    ItemCardComponent,
    PromoCodeComponent,
    HeadsUpComponent,
    RouterModule,
    SvgIconComponent,
    TokenCardEditComponent
  ],
  animations: [
    trigger('inOutAnimation', [
      transition('* => desktopEnter', [
        style({ transform: 'translateX(100%)' }),
        animate('.3s', keyframes([style({ transform: 'translateX(0%)' })]))
      ]),
      transition('* => desktopLeave', [
        style({ transform: 'translateX(0%)' }),
        animate('.3s', keyframes([style({ transform: 'translateX(100%)' })]))
      ]),
      transition('* => mobileEnter', [
        style({ transform: 'translateY(100%)' }),
        animate('.3s', keyframes([style({ transform: 'translateY(0%)' })]))
      ]),
      transition('* => mobileLeave', [
        style({ transform: 'translateY(0%)' }),
        animate('.3s', keyframes([style({ transform: 'translateY(100%)' })]))
      ])
    ]),
    trigger('inOutOpacity', [
      transition('* => overlayEnter', [
        style({ opacity: 0 }),
        animate('.3s', keyframes([style({ opacity: 0.5 })]))
      ]),
      transition('* => overlayLeave', [
        style({ opacity: 0.5 }),
        animate('.3s', keyframes([style({ opacity: 0 })]))
      ])
    ]),
    trigger('warningRepeatPurchaseAnimation', [
      transition('end => start', [
        style({ background: '#f8fafc', border: '1px solid #f1f5f9' }),
        animate(
          '.3s',
          keyframes([
            style({ background: '#fff0f0', border: '1px solid #ffddde' })
          ])
        )
      ]),
      transition('start => end', [
        style({ background: '#fff0f0', border: '1px solid #ffddde' }),
        animate(
          '.3s',
          keyframes([
            style({ background: '#f8fafc', border: '1px solid #f1f5f9' })
          ])
        )
      ])
    ])
  ]
})
export class ModalRepeatPurchaseComponent implements OnInit, OnDestroy {
  @Input() modalShow: boolean | null = false;
  @Input() projectCode: ProjectCode;
  @Input() rafflesInput: Array<Raffle> | null = [];
  @Input() rafflesReveals: Array<RevealRaffle> | null = null;
  @Input() isRepeatPurchase: boolean = false;
  raffles: Array<Raffle> | null = [];

  @Output() toggleModal = new EventEmitter();
  @Output() emitPopulateCartOnClose = new EventEmitter<CartItem[]>();

  @Select(AuthState.user) user$: Observable<User | null>;

  public _alive$ = new Subject<void>();

  // Modal Repeat Purchase
  user: User | null;
  stepModal: number | null = 0;
  processorPayment: string | null = null;
  paymentCards: PaymentCard[] | null = null;
  selectedPaymentCard: PaymentCard | null = null;
  warnMaxTickets: {
    id?: {
      quantity: number;
      show: boolean;
    };
  } | null = {};

  isGettingRaffle: boolean | null = false;
  isGettingPaymentCards: boolean | null = false;
  isGettingMontlySpend: boolean | null = false;

  isProcessing: boolean | null = false;
  isGettingTickets: boolean | null = false;

  // animation config
  // https://www.yearofmoo.com/2017/06/new-wave-of-animation-features.html
  animationState: AnimationStates;
  animationStateTokenEdit: AnimationStates;
  animationStateOverlay: AnimationStates;
  timeoutAnimation = 301;
  warningRepeatPurchaseAnimation: 'start' | 'end' | null = 'end';

  isEditingPaymentCard: boolean = false;
  isBlockReasonToBuy: boolean = false;
  warningPurchaseAboveLimit: boolean = false;
  isMobile = GonativeService.isGonative();

  hardLoading: boolean = false;

  monthlySpend: number | null = 0;
  hasPromoCode: boolean | null = false;

  constructor(
    private userService: UserService,
    private emptyCartService: EmptyCartService,
    public raffleService: RaffleService,
    private window: Window
  ) {}

  ngOnInit(): void {
    if (this.isMobile) {
      this.animationState = AnimationStates.EnterMobile;
    } else {
      this.animationState = AnimationStates.EnterDesktop;
    }
    this.animationStateOverlay = AnimationStates.EnterOverlay;

    this.getAllData();
  }

  ngOnDestroy(): void {
    this._alive$.next();
    this._alive$.complete();
    this.raffles = null;
    this.warnMaxTickets = null;
    this.toggleModal.emit();
  }

  get cart(): Cart {
    return this.emptyCartService.cart;
  }
  get isLoading(): boolean {
    return (
      this.isGettingRaffle ||
      this.isGettingPaymentCards ||
      this.isProcessing ||
      this.isGettingMontlySpend ||
      this.isGettingTickets
    );
  }
  get termsAndConditions(): string {
    return this.projectCode == ProjectCode.SevenDays
      ? '/terms-and-conditions'
      : '/legal/competition-terms-conditions';
  }
  get isBlockedRepeatPurchase(): boolean {
    return (
      this.isRepeatPurchase &&
      this.cart.cached_amount >= 500 &&
      !this.warningPurchaseAboveLimit
    );
  }

  private getAllData() {
    this.user$.pipe(takeUntil(this._alive$)).subscribe(state => {
      this.user = state;
    });
    this.fillRaffles();
    this.createCart();
    this.getPaymentCards();
    this.getMonthlySpend();
  }
  private getMonthlySpend() {
    this.isGettingMontlySpend = true;
    this.userService
      .getCurrentMonthlySpend()
      .pipe(takeUntil(this._alive$))
      .subscribe({
        next: response => {
          this.monthlySpend = response.monthly_spend;
        }
      })
      .add(() => {
        this.isGettingMontlySpend = false;
      });
  }
  private getPaymentCards() {
    this.isGettingPaymentCards = true;
    this.userService
      .getCurrentPaymentCads()
      .pipe(takeUntil(this._alive$))
      .subscribe({
        next: response => {
          this.paymentCards = response;
          this.selectedPaymentCard = response[0] ?? null;
        }
      })
      .add(() => {
        this.isGettingPaymentCards = false;
      });
  }
  private fillRaffles() {
    const reducedIDS: Array<number> = this.rafflesInput.map(rafflesId =>
      Number(rafflesId.id)
    );
    this.rafflesReveals.forEach(raffle => {
      if (!reducedIDS.includes(raffle.id)) {
        this.isGettingRaffle = true;
        this.raffleService
          .getGameBySlug(raffle.slug, 'GBP')
          .pipe(takeUntil(this._alive$))
          .subscribe({
            next: response => {
              this.raffles.push(response);
            }
          })
          .add(() => {
            this.isGettingRaffle = false;
          });
      } else {
        this.raffles.push(
          this.rafflesInput.filter(rafflesId => rafflesId.id == raffle.id)[0]
        );
        this.isGettingRaffle = false;
      }
    });
  }
  private createCart() {
    this.hardLoading = true;
    return this.emptyCartService
      .createCart()
      .pipe(
        switchMap(() => {
          return from(this.rafflesReveals).pipe(
            concatMap(raffles =>
              this.emptyCartService
                .addDrawItem(raffles.id, raffles.quantity)
                .pipe(
                  takeUntil(this._alive$),
                  catchError(err => {
                    this.handleTicketsQuantityError(raffles.id, err.error);
                    return of(null);
                  })
                )
            )
          );
        })
      )
      .subscribe({
        complete: () => {
          this.hardLoading = false;
        }
      });
  }

  postCartAddPopupRaffle(data: { raffleId: number; quantity: number }) {
    this.isGettingTickets = true;
    this.emptyCartService
      .addDrawItem(data.raffleId, data.quantity)
      .pipe(takeUntil(this._alive$))
      .subscribe({
        error: err => {
          this.handleTicketsQuantityError(data.raffleId, err.error);
        }
      })
      .add(() => {
        this.isGettingTickets = false;
      });
  }

  private handleTicketsQuantityError(
    raffleId: number,
    error: { code: number; string: string; quantity: number }
  ) {
    if (error.code == 40002 || error.code == 41003 || error.code == 20017) {
      this.isGettingTickets = false;
      return this.emptyCartService
        .addDrawItem(raffleId, error.quantity)
        .pipe(takeUntil(this._alive$))
        .subscribe({
          next: _ => {
            this.warnMaxTickets[raffleId] = {
              error: "You've hit the purchase limit (" + error.quantity + ')!',
              quantity: error.quantity,
              show: true
            };
          },
          error: err => {
            this.warnMaxTickets[raffleId] = {
              quantity: error.quantity,
              show: true
            };
            this.handleTicketsQuantityError(raffleId, err.error);
            this.isGettingTickets = false;
          }
        })
        .add(() => {
          this.isGettingTickets = false;
        });
    }
  }

  private handlesubmissionData(paymentType: string): PaymentSubmission {
    let submissionData = new PaymentSubmission();
    submissionData.returnPath =
      this.window.location.origin +
      '/checkout/order-received/_ORDER_?key=_ORDERKEY_';

    if (this.isMobile) {
      submissionData.returnPath += '&loadapp=true';
    }

    if (paymentType == 'card') {
      if (this.user.suggested_card_method !== null) {
        submissionData.preferredProcessor = this.user.suggested_card_method;
      } else {
        submissionData.preferredProcessor =
          (paymentType as PaymentGatewayName) || 'checkout';
      }
    }
    else {
      submissionData.preferredProcessor = paymentType as PaymentGatewayName;
    }

    if (this.selectedPaymentCard) {
      if (this.selectedPaymentCard.id == 'new') {
        this.selectedPaymentCard.id = null;
      }

      if (
        this.selectedPaymentCard.id &&
        submissionData.preferredProcessor == 'checkout'
      ) {
        submissionData.paymentCardId = this.selectedPaymentCard.id;
        submissionData.flags = ['skip_to_gateway'];
      }
    }

    return submissionData;
  }
  handleRepeatPurchaseWarning(event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.warningPurchaseAboveLimit = isChecked;
    this.warningRepeatPurchaseAnimation = isChecked ? 'end' : 'start';
  }
  handleNewPaymentMethod() {
    this.selectedPaymentCard.id = 'new';
    this.handlePayment('card');
  }

  handlePayment(paymentType: string) {
    if (this.isRepeatPurchase && this.isBlockedRepeatPurchase) {
      this.warningRepeatPurchaseAnimation = 'start';
      return;
    }

    if (this.cart.cached_amount == 0) {
      paymentType = 'free';
    }
    this.isProcessing = true;

    return this.emptyCartService
      .postCartPayment(
        this.cart.id,
        String(this.cart.cached_amount),
        this.handlesubmissionData(paymentType),
        this.paymentCards.length > 0 ? 'instant' : 'buy-now'
      )
      .pipe(takeUntil(this._alive$))
      .subscribe({
        next: response => {
          if (paymentType == 'free') {
            this.window.location.href =
              this.window.location.origin +
              '/checkout/order-pay/redirect?openbrowser=true&target_url=' +
              encodeURIComponent(response.gateway_url);
          } else {
            this.window.location.href = response.gateway_url;
          }
        }
      })
      .add(() => {
        this.isProcessing = false;
      });
  }

  closeAllModal() {
    this.hideTokenEditModal();
    this.closeModal();
  }
  closeModal() {
    if (this.isMobile) {
      this.animationState = AnimationStates.LeaveMobile;
    } else {
      this.animationState = AnimationStates.LeaveDesktop;
    }
    this.animationStateOverlay = AnimationStates.LeaveOverlay;
    setTimeout(() => {
      this.ngOnDestroy();
    }, this.timeoutAnimation);

    this.emitPopulateCartOnClose.emit(this.cart.items);
  }

  deleteCartItem(cartItemId: string) {
    this.emptyCartService
      .deleteItem(cartItemId)
      .pipe(takeUntil(this._alive$))
      .subscribe({
        next: _ => {
          if (this.cart.items.length == 0) {
            this.closeModal();
          }
        }
      })
      .add(() => {
        this.isGettingTickets = false;
      });
  }

  handleChangeQuantity(
    data: {
      raffleId: number;
      cartItemID: string;
      quantity: number;
      error?: string;
    },
    skipClear: boolean | null = false
  ) {
    if (!skipClear) this.warnMaxTickets[data.raffleId] = { show: false };

    this.isGettingTickets = true;

    if (data.quantity == 0) return this.deleteCartItem(data.cartItemID);

    this.emptyCartService
      .patchItem(data.cartItemID, data.quantity)
      .pipe(takeUntil(this._alive$))
      .subscribe({
        error: error => {
          this.warnMaxTickets[data.raffleId] = {
            quantity: error.error.quantity,
            error:
              "You've hit the purchase limit (" + error.error.quantity + ')!',
            show: true
          };

          this.handleChangeQuantity(
            {
              raffleId: data.raffleId,
              cartItemID: data.cartItemID,
              quantity: error.error.quantity
            },
            true
          );
        }
      })
      .add(() => {
        this.isGettingTickets = false;
        if(this.user.monthly_spend_limit) {
          this.handleMaxMonthlyReached(
            Number(this.cart.cached_amount) + this.monthlySpend >
            Number(this.user.monthly_spend_limit)
          );
        }
      });
  }
  showTokenEditModal() {
    if (this.isMobile) {
      this.animationStateTokenEdit = AnimationStates.EnterMobile;
      this.animationState = AnimationStates.LeaveMobile;
    } else {
      this.animationStateTokenEdit = AnimationStates.EnterDesktop;
      this.animationState = AnimationStates.LeaveDesktop;
    }
    setTimeout(() => {
      this.stepModal = 1;
    }, this.timeoutAnimation);
  }
  hideTokenEditModal() {
    if (this.isMobile) {
      this.animationStateTokenEdit = AnimationStates.LeaveMobile;
      this.animationState = AnimationStates.EnterMobile;
    } else {
      this.animationStateTokenEdit = AnimationStates.LeaveDesktop;
      this.animationState = AnimationStates.EnterDesktop;
    }
    setTimeout(() => {
      this.stepModal = 0;
    }, this.timeoutAnimation);
  }

  handleMaxMonthlyReached(value: boolean) {
    this.isBlockReasonToBuy = value;
  }
  handleChangePaymentCard(value: PaymentCard | null) {
    this.selectedPaymentCard = value;
  }
}

export enum AnimationStates {
  EnterMobile = 'mobileEnter',
  LeaveMobile = 'mobileLeave',
  EnterDesktop = 'desktopEnter',
  LeaveDesktop = 'desktopLeave',
  EnterOverlay = 'overlayEnter',
  LeaveOverlay = 'overlayLeave'
}
